import { CarrierType, OrderResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { HandleOrderPageLocationState, ReturnPathAdditionalParams } from '@planning/pages/Order'
import {
  getCheckinContainerPagePath,
  getCheckoutContainerPagePath,
  getJourneyOrderPagePath,
  getVisitOrderPagePath,
  getVisitOrderPageReturnPath,
  orderDirectionToOrderNavigationDirection,
} from '@planning/utils/visit-utils'
import { useTranslate } from '@tolgee/react'
import { PATHS } from '@tom-ui/utils'
import { useNavigate } from 'react-router'

export type OrderNavigationDirection = 'load' | 'discharge' | 'restow'

export const useOrderNavigation = () => {
  const navigate = useNavigate()
  const { t } = useTranslate()
  const { appViewStore } = usePlanningStore()

  const navigateOrderPageByOrder = async (
    order: OrderResponseDto,
    direction?: OrderNavigationDirection,
  ) => {
    navigateVisitOrderPage({
      direction: direction ?? orderDirectionToOrderNavigationDirection(order.direction),
      carrierVisitType: order.carrierVisitType,
      carrierVisitId: order.carrierVisitId,
      orderId: order.id,
    })
  }

  const navigateVisitOrderPage = async (props: {
    direction: OrderNavigationDirection
    carrierVisitType?: CarrierType
    carrierVisitId?: number | null
    orderId?: number
  }) => {
    const { carrierVisitType, carrierVisitId, direction, orderId } = props

    if (!carrierVisitType || !carrierVisitId) {
      appViewStore.setShowAlert('error', t('failedToEditOrder', 'Failed to edit order'))
      return
    }

    const orderPath = getVisitOrderPagePath(
      carrierVisitType,
      carrierVisitId,
      direction,
      orderId ?? 'new',
    )
    const returnPath = getVisitOrderPageReturnPath(carrierVisitType, direction, carrierVisitId)

    const state: HandleOrderPageLocationState = {
      returnPath,
    }

    navigate(orderPath, { state })
  }

  const navigateJourneyOrderPage = async (props?: {
    orderId?: number | null
    containerId?: number | null
    returnPathAdditionalParams?: ReturnPathAdditionalParams
  }) => {
    const { orderId, containerId, returnPathAdditionalParams } = props ?? {}
    let orderPath = getJourneyOrderPagePath(orderId ?? 'new')

    if (containerId) {
      orderPath += `?containerId=${containerId}`
    }

    const returnPath = PATHS.visits.containerVisits.url

    const state: HandleOrderPageLocationState = {
      returnPath,
      returnPathAdditionalParams,
    }

    navigate(orderPath, { state })
  }

  const navigateBookingOrderPage = async (order: OrderResponseDto) => {
    const state: HandleOrderPageLocationState = {
      returnPath: `${PATHS.orders.bookings.url}/${order.referenceNumber}/visit/${order.carrierVisitId}/orders`,
    }

    navigate(
      `${PATHS.orders.bookings.url}/${order.referenceNumber}/visit/${order.carrierVisitId}/orders/${order.id}`,
      { state },
    )
  }

  const navigateCheckoutContainerPage = async (order: OrderResponseDto) => {
    if (!order.carrierVisitType || !order.carrierVisitId) {
      appViewStore.setShowAlert('error', t('failedToCheckout', 'Failed to access checkout order'))
      return
    }

    const { carrierVisitType, carrierVisitId, direction, id: orderId } = order

    const orderNavigationDirection = orderDirectionToOrderNavigationDirection(direction)

    const checkoutPath = getCheckoutContainerPagePath(carrierVisitType, carrierVisitId, orderId)
    const returnPath = getVisitOrderPageReturnPath(
      carrierVisitType,
      orderNavigationDirection,
      carrierVisitId,
    )

    const state: HandleOrderPageLocationState = {
      returnPath,
    }

    navigate(checkoutPath, { state })
  }

  const navigateCheckoutContainerVisitsPage = async (order: OrderResponseDto) => {
    const state: HandleOrderPageLocationState = {
      returnPath: `${PATHS.visits.containerVisits.url}/`,
    }

    navigate(
      `${PATHS.visits.containerVisits.url}/${order.carrierVisitId ?? 0}/checkout/${order.id}`,
      { state },
    )
  }

  const navigateCheckinContainerVisitsPage = async (order: OrderResponseDto) => {
    const state: HandleOrderPageLocationState = {
      returnPath: `${PATHS.visits.containerVisits.url}/`,
    }

    navigate(
      `${PATHS.visits.containerVisits.url}/${order.carrierVisitId ?? 0}/checkin/${order.id}`,
      { state },
    )
  }

  const navigateCheckinContainerPage = async (order: OrderResponseDto) => {
    if (!order.carrierVisitType || !order.carrierVisitId) {
      appViewStore.setShowAlert('error', t('failedToEditOrder', 'Failed to access checkin order'))
      return
    }

    const { carrierVisitType, carrierVisitId, direction, id: orderId } = order

    const orderNavigationDirection = orderDirectionToOrderNavigationDirection(direction)

    const checkinPath = getCheckinContainerPagePath(carrierVisitType, carrierVisitId, orderId)
    const returnPath = getVisitOrderPageReturnPath(
      carrierVisitType,
      orderNavigationDirection,
      carrierVisitId,
    )

    const state: HandleOrderPageLocationState = {
      returnPath,
    }

    navigate(checkinPath, { state })
  }

  const openEditOrder = async (order: OrderResponseDto, direction?: OrderNavigationDirection) => {
    navigateOrderPageByOrder(order, direction)
  }

  const navigateAddEmptyRailcarPage = (
    visitId: number | string,
    direction: OrderNavigationDirection,
  ) => {
    const path = `${PATHS.visits.railVisits.url}/${visitId}/${direction}/railcar/new`

    const returnPath = `${PATHS.visits.railVisits.url}/${visitId}/${direction}`

    const state: HandleOrderPageLocationState = {
      returnPath,
    }

    navigate(path, { state })
  }

  return {
    navigateVisitOrderPage,
    navigateJourneyOrderPage,
    openEditOrder,
    navigateBookingOrderPage,
    navigateCheckinContainerPage,
    navigateCheckoutContainerPage,
    navigateCheckoutContainerVisitsPage,
    navigateCheckinContainerVisitsPage,
    navigateAddEmptyRailcarPage,
  }
}
